import { Modal, Stack, Box, Typography, IconButton } from "@mui/material";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { desktopSx } from "src/utils/styles/modalStyles";
import CloseIcon from "@mui/icons-material/Close";
import PasswordField from "src/components/PasswordField";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { supabaseClient } from "src/supabase/supabaseClient";
import { MuiOtpInput } from "mui-one-time-password-input";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UpdatePasswordModal = ({ open, onClose, onSuccess }: Props) => {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [requireOTP, setRequireOTP] = useState(false);
  const [otp, setOTP] = useState("");

  const handlePassword = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  }, []);

  const handleOTP = useCallback((value: string) => {
    setOTP(value);
  }, []);

  const handleConfirmPassword = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setConfirmPassword(event.target.value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const trimmedPassword = password.trim();
      const trimmedConfirmPassword = confirmPassword.trim();

      if (trimmedPassword != trimmedConfirmPassword) {
        setError("Looks like one of the passwords is incorrect");
        return;
      }

      setLoading(true);

      try {
        const { data, error } = await supabaseClient.auth.updateUser({
          password: trimmedPassword,
          ...(otp && { nonce: otp }),
        });

        if (error?.code === "reauthentication_needed") {
          await supabaseClient.auth.reauthenticate();
          setRequireOTP(true);
          return;
        }

        if (error != null) {
          setError(error.message);
          return;
        }

        if (data.user != null) {
          setPassword("");
          setConfirmPassword("");
          onSuccess?.();
        }
      } catch (err) {
        console.error("Unexpected error", err);
      } finally {
        setLoading(false);
      }
    },
    [confirmPassword, onSuccess, otp, password],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={5}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Update Password
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>

        <Stack gap={2}>
          <PasswordField
            value={password}
            onChange={handlePassword}
            id="filled-adornment-password"
          />
          <PasswordField
            value={confirmPassword}
            onChange={handleConfirmPassword}
            id="filled-adornment-confirm-password"
            label="Confirm Password"
          />
          {requireOTP ? (
            <Stack gap={2} alignItems="center">
              <Typography variant="h6">Check your email for OTP</Typography>
              <MuiOtpInput length={6} value={otp} onChange={handleOTP} />
            </Stack>
          ) : null}
        </Stack>

        {error ? (
          <Typography sx={{ color: "red" }} textAlign="center">
            {error}
          </Typography>
        ) : null}
        <LoadingButton loading={loading} type="submit" disabled={loading}>
          Submit New Password
        </LoadingButton>
      </Stack>
    </Modal>
  );
};

export default UpdatePasswordModal;
